import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { useMarketStore } from '../store/marketStore';
import { formatPct, getPriceColor } from '../utils/format';
import { fetchGlobalLock, GlobalLockInfo } from '../api/market';

/**
 * 大盘状态条组件
 * 显示：上涨家/下跌家/平盘 + 上证/深证/创业板涨跌 + 今日整体条件锁状态
 */
export default function MarketStatusBar() {
  const { breadth } = useMarketStore();
  const [lock, setLock] = useState<GlobalLockInfo | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => fetchGlobalLock().then((d) => { if (alive) setLock(d); }).catch(() => {});
    load();
    const id = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  if (!breadth) {
    return (
      <Box sx={{ bgcolor: '#263238', color: '#fff', py: 0.5, px: 3, fontSize: 13 }}>
        <Typography variant="caption" sx={{ opacity: 0.7 }}>
          正在获取大盘数据...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          bgcolor: '#263238',
          color: '#fff',
          py: 0.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 3,
          fontSize: 13,
          flexWrap: 'wrap',
        }}
      >
        {/* 涨跌家数 */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            涨跌家数:
          </Typography>
          <span style={{ color: '#f44336' }}>↑{breadth.upCount}</span>
          <span style={{ color: '#4caf50' }}>↓{breadth.downCount}</span>
          <span style={{ color: '#9e9e9e' }}>→{breadth.flatCount}</span>
        </Box>

        {/* 指数 */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <IndexItem label="上证" value={breadth.shIndex} changePct={breadth.shChangePct} />
          <IndexItem label="深证" value={breadth.szIndex} changePct={breadth.szChangePct} />
          <IndexItem label="创业板" value={breadth.cybIndex} changePct={breadth.cybChangePct} />
        </Box>

        {/* 锁状态徽章 */}
        {lock && lock.locked && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', bgcolor: 'error.main', px: 1, py: 0.25, borderRadius: 1 }}>
            <CircularProgress size={10} sx={{ color: '#fff' }} />
            <Typography variant="caption" sx={{ fontWeight: 600 }}>今日已锁定</Typography>
          </Box>
        )}

        {/* 更新时间 */}
        <Box sx={{ ml: 'auto', opacity: 0.5 }}>
          <Typography variant="caption">
            {breadth.snapshotTime ? new Date(breadth.snapshotTime).toLocaleTimeString('zh-CN') : ''}
          </Typography>
        </Box>
      </Box>

      {/* 整体条件触发 → 当日禁止买卖（双向） */}
      {(lock?.locked || breadth.pauseBuy || breadth.pauseSell) && (
        <Alert severity="error" sx={{ borderRadius: 0, py: 0.5 }}>
          🔒 今日整体条件触发，全日禁止买入和卖出
          {lock?.reasons && lock.reasons.length > 0 && (
            <Typography variant="caption" component="div" sx={{ mt: 0.25, opacity: 0.9 }}>
              原因：{lock.reasons.join('；')}
            </Typography>
          )}
        </Alert>
      )}
    </Box>
  );
}

/** 指数项 */
function IndexItem({ label, value, changePct }: { label: string; value: number | null | undefined; changePct: number | null | undefined }) {
  const color = getPriceColor(changePct ?? 0);
  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {label}
      </Typography>
      <span style={{ fontWeight: 600 }}>{(value ?? 0).toFixed(2)}</span>
      <span style={{ color }}>{formatPct(changePct ?? 0)}</span>
    </Box>
  );
}
